import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useAuth } from "../../auth";
import ListLayout from "../../components/Layout/ListLayout";
import { TablePagination } from "@mui/material";
import { customInstanceDocumentos } from "../../api/axios-client";
import { BinList } from "./documents/components/BinList";
import { BinFilter } from "./documents/components/BinFilter";
import { RestoreDocumentDialog } from "./documents/components/RestoreDocumentDialog";
import { useTranslation } from "react-i18next";

const binSearchSchema = z.object({
    page: z.number().default(1),
    size: z.number().default(10),
    q: z.string().optional(),
    code: z.string().optional(),
    createdAfter: z.string().optional(),
    createdBefore: z.string().optional(),
    author: z.string().optional(),
    byArchivalClassificationId: z.string().optional(),
    subDocumentTypeId: z.string().optional(),
});

export const Route = createFileRoute('/__auth/bin')({
    component: BinPage,
    staticData: {
        title: "routes.bin",
    },
    validateSearch: binSearchSchema,
});

function BinPage() {
    const { t } = useTranslation();
    const { currentTenant } = useAuth();
    const search = Route.useSearch();
    const navigate = useNavigate({ from: Route.fullPath });

    const [localFilters, setLocalFilters] = useState({
        q: search.q || "",
        code: search.code || "",
        createdAfter: search.createdAfter || "",
        createdBefore: search.createdBefore || "",
        author: search.author || "",
        byArchivalClassificationId: search.byArchivalClassificationId || "",
        subDocumentTypeId: search.subDocumentTypeId || "",
    });

    const [restoreDialog, setRestoreDialog] = useState<{ open: boolean; id: number | null; code: string }>({
        open: false,
        id: null,
        code: ""
    });

    const tenantId = currentTenant || "default-domain";

    const { data: pageData, isLoading, error } = useQuery({
        queryKey: ['bin-documents', tenantId, search.page, search.size, search.q, search.createdAfter, search.createdBefore],
        queryFn: async () => {
            const params = new URLSearchParams();
            params.append('page', String((search.page || 1) - 1));
            params.append('size', String(search.size || 10));
            params.append('sort', 'createdAt,desc');

            if (search.q) params.append('name', search.q);
            if (search.code) params.append('code', search.code);
            if (search.author) params.append('author', search.author);
            if (search.byArchivalClassificationId) params.append('byArchivalClassificationId', search.byArchivalClassificationId);
            if (search.subDocumentTypeId) params.append('subDocumentTypeId', search.subDocumentTypeId);

            if (search.createdAfter) {
                const afterDate = new Date(search.createdAfter);
                if (!isNaN(afterDate.getTime())) {
                    params.append('createdAfter', afterDate.toISOString());
                }
            }
            if (search.createdBefore) {
                const beforeDate = new Date(search.createdBefore);
                if (!isNaN(beforeDate.getTime())) {
                    beforeDate.setHours(23, 59, 59);
                    params.append('createdBefore', beforeDate.toISOString());
                }
            }

            const url = `/${tenantId}/documents/excluded?${params.toString()}`;
            return customInstanceDocumentos<any>(url, {
                method: 'GET',
                headers: { tenant: tenantId }
            });
        },
        enabled: !!tenantId,
    });

    const handleFilterChange = (field: keyof typeof localFilters, value: string) => {
        setLocalFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleSearch = () => {
        navigate({
            search: (prev) => ({
                ...prev,
                q: localFilters.q || undefined,
                code: localFilters.code || undefined,
                createdAfter: localFilters.createdAfter || undefined,
                createdBefore: localFilters.createdBefore || undefined,
                author: localFilters.author || undefined,
                byArchivalClassificationId: localFilters.byArchivalClassificationId || undefined,
                subDocumentTypeId: localFilters.subDocumentTypeId || undefined,
                page: 1
            })
        });
    };

    const handleClear = () => {
        setLocalFilters({ q: "", code: "", createdAfter: "", createdBefore: "", author: "", byArchivalClassificationId: "", subDocumentTypeId: "" });
        navigate({ search: (prev) => ({ ...prev, q: undefined, code: undefined, createdAfter: undefined, createdBefore: undefined, author: undefined, byArchivalClassificationId: undefined, subDocumentTypeId: undefined, page: 1 }) });
    };

    const handlePageChange = (_: unknown, newPage: number) => {
        navigate({ search: (prev) => ({ ...prev, page: newPage + 1 }) });
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        navigate({ search: (prev) => ({ ...prev, size: parseInt(event.target.value, 10), page: 1 }) });
    };

    const dataList = pageData?.content || [];
    const totalElements = pageData?.totalElements || 0;

    return (
        <ListLayout
            title={t("bin.title")}
            subtitle={t("bin.subtitle")}
            isLoading={isLoading}
            error={error}
            isEmpty={false}
        >
            <BinFilter
                filters={localFilters}
                onChange={handleFilterChange}
                onSearch={handleSearch}
                onClear={handleClear}
            />

            <BinList
                data={dataList}
                onRestore={(doc) => setRestoreDialog({ open: true, id: doc.id, code: doc.code || doc.description })}
            />

            {dataList.length > 0 && (
                <TablePagination
                    component="div"
                    count={totalElements}
                    page={(search.page || 1) - 1}
                    onPageChange={handlePageChange}
                    rowsPerPage={search.size || 10}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage={t("common.itemsPerPage")}
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
            )}

            <RestoreDocumentDialog
                open={restoreDialog.open}
                documentId={restoreDialog.id}
                documentCode={restoreDialog.code}
                onClose={() => setRestoreDialog({ open: false, id: null, code: "" })}
            />
        </ListLayout>
    );
}
